import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';

const MIN_PRESS_VISIBLE_MS = 180;

interface UseButtonPressControlsInput {
  setIsHovered: Dispatch<SetStateAction<boolean>>;
  setIsPressed: Dispatch<SetStateAction<boolean>>;
}

export interface ButtonPressControls {
  beginPress: () => void;
  releasePress: () => void;
  cancelPress: () => void;
}

export function useButtonPressControls({
  setIsHovered,
  setIsPressed,
}: UseButtonPressControlsInput): ButtonPressControls {
  const pressedAtRef = useRef<number>(0);
  const pressResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPendingPressReset = useCallback((): void => {
    if (pressResetTimerRef.current) {
      clearTimeout(pressResetTimerRef.current);
      pressResetTimerRef.current = null;
    }
  }, []);

  const finishPress = useCallback((): void => {
    setIsPressed(false);
    setIsHovered(false);
  }, [setIsHovered, setIsPressed]);

  const releasePress = useCallback((): void => {
    const elapsed = performance.now() - pressedAtRef.current;
    const remaining = Math.max(0, MIN_PRESS_VISIBLE_MS - elapsed);
    if (remaining === 0) {
      finishPress();
      return;
    }

    clearPendingPressReset();
    pressResetTimerRef.current = setTimeout(() => {
      finishPress();
      pressResetTimerRef.current = null;
    }, remaining);
  }, [clearPendingPressReset, finishPress]);

  const beginPress = useCallback((): void => {
    clearPendingPressReset();
    pressedAtRef.current = performance.now();
    setIsPressed(true);
  }, [clearPendingPressReset, setIsPressed]);

  const cancelPress = useCallback((): void => {
    clearPendingPressReset();
    finishPress();
  }, [clearPendingPressReset, finishPress]);

  useEffect(() => () => clearPendingPressReset(), [clearPendingPressReset]);

  return {
    beginPress,
    releasePress,
    cancelPress,
  };
}
